'use client'

import {
    approveTransfer,
    cancelTransfer,
    FinancialTransfer,
    FinancialTransferStatus,
    getTransfer,
    rejectTransfer,
} from '@/lib/api/financial/transfer-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useIsFinanceOperator } from '@/lib/hooks/use-is-finance-operator'
import { useAuth } from '@/store/hooks'
import PasswordField from '../payment-account/components/password-field'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCancel,
    MdCheck,
    MdClose,
    MdInfoOutline,
    MdSwapHoriz,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABEL: Record<FinancialTransferStatus, string> = {
    PENDING_APPROVAL: 'Aguardando aprovação',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
    PROCESSING: 'Processando',
    DONE: 'Concluída',
    FAILED: 'Falhou',
    CANCELLED: 'Cancelada',
}

const STATUS_BADGE: Record<FinancialTransferStatus, string> = {
    PENDING_APPROVAL: 'badge-warning',
    APPROVED: 'badge-info',
    REJECTED: 'badge-error',
    PROCESSING: 'badge-info',
    DONE: 'badge-success',
    FAILED: 'badge-error',
    CANCELLED: 'badge-ghost',
}

const DESTINATION_LABEL: Record<string, string> = {
    PIX: 'Chave PIX avulsa',
    BANK_ACCOUNT: 'Conta bancária cadastrada',
    ORG_OWN_BANK: 'Conta própria da empresa',
    PARTNER: 'Parceiro',
    PROFESSIONAL: 'Profissional',
    SUPPLIER: 'Fornecedor',
}

const KIND_LABEL: Record<string, string> = {
    STANDALONE: 'Transferência avulsa',
    BILLING_PAYMENT: 'Pagamento de conta a pagar',
}

const formatBrl = (cents: number, withSign = false): string => {
    const v = (Math.abs(cents) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
    if (!withSign) return v
    return cents < 0 ? `-${v}` : v
}

const formatDateTime = (iso: string | null | undefined): string =>
    iso
        ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
        : '—'

export default function TransferDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canUpdate: canUpdateTransfers } = useResourcePermissions('financial', 'transfers')
    const { canUpdate: canApprove } = useResourcePermissions('financial', 'transfer_approvals')
    const { isOperator, hasOperators, loading: loadingOperator } = useIsFinanceOperator()
    const [transfer, setTransfer] = useState<FinancialTransfer | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [showApprove, setShowApprove] = useState(false)
    const [showReject, setShowReject] = useState(false)
    const [showCancel, setShowCancel] = useState(false)
    const [password, setPassword] = useState('')
    const [reason, setReason] = useState('')
    const [cancelReason, setCancelReason] = useState('')
    const [processing, setProcessing] = useState(false)

    const refetch = async () => {
        if (!id) return
        const res = await getTransfer(Number(id))
        if (res.success && res.result) setTransfer(res.result)
    }

    useEffect(() => {
        if (!id) return
        ;(async () => {
            setLoading(true)
            try {
                const res = await getTransfer(Number(id))
                if (res.success && res.result) setTransfer(res.result)
                else setError(res.error || 'Transferência não encontrada')
            } finally {
                setLoading(false)
            }
        })()
    }, [id])

    const handleApprove = async () => {
        if (!transfer) return
        if (!password) return toast.warning('Confirme sua senha para aprovar')
        setProcessing(true)
        try {
            const res = await approveTransfer(transfer.id, password)
            if (res.success) {
                toast.success('Transferência aprovada')
                setShowApprove(false)
                setPassword('')
                await refetch()
            } else {
                toast.error(res.error || 'Erro ao aprovar')
            }
        } finally {
            setProcessing(false)
        }
    }

    const handleReject = async () => {
        if (!transfer) return
        if (!reason.trim()) return toast.warning('Informe o motivo da rejeição')
        setProcessing(true)
        try {
            const res = await rejectTransfer(transfer.id, reason.trim())
            if (res.success) {
                toast.success('Transferência rejeitada')
                setShowReject(false)
                setReason('')
                await refetch()
            } else {
                toast.error(res.error || 'Erro ao rejeitar')
            }
        } finally {
            setProcessing(false)
        }
    }

    const handleCancel = async () => {
        if (!transfer) return
        setProcessing(true)
        try {
            const res = await cancelTransfer(transfer.id, cancelReason.trim() || undefined)
            if (res.success) {
                toast.success('Transferência cancelada')
                setShowCancel(false)
                setCancelReason('')
                await refetch()
            } else {
                toast.error(res.error || 'Erro ao cancelar')
            }
        } finally {
            setProcessing(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (error || !transfer) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <button className="btn btn-ghost btn-sm mb-4" onClick={() => navigate(-1)}>
                    <MdArrowBack /> Voltar
                </button>
                <div className="alert alert-error">{error || 'Transferência não encontrada'}</div>
            </div>
        )
    }

    const isPending = transfer.status === 'PENDING_APPROVAL'
    const isOwner = !!user?.id && transfer.requestedById === user.id
    const canCancel = isPending && (isOwner || canApprove || canUpdateTransfers)
    // Aprovar/Rejeitar exige permission + ser operador financeiro eleito
    const canShowApprove = isPending && canApprove && isOperator
    const canShowReject = isPending && canApprove && isOperator
    const showOperatorWarning =
        isPending && canApprove && !loadingOperator && !isOperator && hasOperators

    return (
        <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                    <MdArrowBack /> Voltar
                </button>
                {isPending && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canCancel && (
                            <button
                                className="btn btn-sm btn-ghost gap-1"
                                onClick={() => setShowCancel(true)}
                                disabled={processing}
                                title={isOwner ? 'Cancelar minha solicitação' : 'Cancelar solicitação pendente'}
                            >
                                <MdCancel /> Cancelar
                            </button>
                        )}
                        {canShowReject && (
                            <button
                                className="btn btn-sm btn-error btn-outline gap-1"
                                onClick={() => setShowReject(true)}
                                disabled={processing}
                            >
                                <MdClose /> Rejeitar
                            </button>
                        )}
                        {canShowApprove && (
                            <button
                                className="btn btn-sm btn-success gap-1"
                                onClick={() => setShowApprove(true)}
                                disabled={processing}
                            >
                                <MdCheck /> Aprovar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {showOperatorWarning && (
                <div className="alert alert-warning">
                    <MdInfoOutline className="text-xl" />
                    <span className="text-sm">
                        Apenas operadores financeiros eleitos podem aprovar ou rejeitar transferências. Você pode visualizar mas não executar essas ações.
                    </span>
                </div>
            )}

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-xl font-bold flex items-center gap-2">
                                <MdSwapHoriz /> Transferência #{transfer.id}
                            </h1>
                            <p className="text-xs text-base-content/60 mt-1">
                                Solicitada em {formatDateTime(transfer.createdAt)}
                            </p>
                        </div>
                        <span className={`badge ${STATUS_BADGE[transfer.status]}`}>
                            {STATUS_LABEL[transfer.status]}
                        </span>
                    </div>

                    <div className="divider my-2" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <Field label="Valor">
                            <span className="font-mono font-bold text-lg text-error">
                                {formatBrl(transfer.amount)}
                            </span>
                        </Field>
                        <Field label="Tipo">{KIND_LABEL[transfer.kind] ?? transfer.kind}</Field>
                        <Field label="Destino">{DESTINATION_LABEL[transfer.destinationType] ?? transfer.destinationType}</Field>
                        {transfer.scheduledFor && (
                            <Field label="Agendada para">{formatDateTime(transfer.scheduledFor)}</Field>
                        )}
                        <Field label="Descrição" wide>
                            {transfer.description || '—'}
                        </Field>
                    </div>
                </div>
            </div>

            {/* Dados do destinatário */}
            {(transfer.pixKey ||
                transfer.bankAccountId ||
                transfer.partnerId ||
                transfer.professionalId ||
                transfer.supplierId ||
                transfer.bankCode) && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-base">Destinatário</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {transfer.partnerId && <Field label="Parceiro">#{transfer.partnerId}</Field>}
                            {transfer.professionalId && (
                                <Field label="Profissional">#{transfer.professionalId}</Field>
                            )}
                            {transfer.supplierId && <Field label="Fornecedor">#{transfer.supplierId}</Field>}
                            {transfer.bankAccountId && (
                                <Field label="Conta bancária">#{transfer.bankAccountId}</Field>
                            )}
                            {transfer.pixKeyType && (
                                <Field label="Tipo da chave">{transfer.pixKeyType}</Field>
                            )}
                            {transfer.pixKey && (
                                <Field label="Chave PIX" wide>
                                    <span className="font-mono">{transfer.pixKey}</span>
                                </Field>
                            )}
                            {transfer.bankCode && <Field label="Banco">{transfer.bankCode}</Field>}
                            {transfer.bankAgency && <Field label="Agência">{transfer.bankAgency}</Field>}
                            {transfer.bankAccountNumber && (
                                <Field label="Conta">{transfer.bankAccountNumber}</Field>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Aprovação e execução */}
            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <h2 className="card-title text-base">Aprovação e execução</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <Field label="Solicitada por">
                            {transfer.requestedBy?.name
                                ? `${transfer.requestedBy.name}${transfer.requestedBy.email ? ` (${transfer.requestedBy.email})` : ''}`
                                : `#${transfer.requestedById}`}
                        </Field>
                        {transfer.approvedById && (
                            <Field label="Aprovada por">
                                {transfer.approvedBy?.name
                                    ? `${transfer.approvedBy.name}${transfer.approvedBy.email ? ` (${transfer.approvedBy.email})` : ''}`
                                    : `#${transfer.approvedById}`}
                            </Field>
                        )}
                        {transfer.approvedAt && (
                            <Field label="Aprovada em">{formatDateTime(transfer.approvedAt)}</Field>
                        )}
                        {transfer.executedAt && (
                            <Field label="Executada em">{formatDateTime(transfer.executedAt)}</Field>
                        )}
                        {transfer.providerTransferId && (
                            <Field label="ID externo" wide>
                                <span className="font-mono text-xs">{transfer.providerTransferId}</span>
                            </Field>
                        )}
                        {transfer.rejectedReason && (
                            <Field label="Motivo da rejeição" wide>
                                <span className="text-error">{transfer.rejectedReason}</span>
                            </Field>
                        )}
                        {transfer.failReason && (
                            <Field label="Motivo da falha" wide>
                                <span className="text-error">{transfer.failReason}</span>
                            </Field>
                        )}
                        {transfer.billingId && (
                            <Field label="Conta a pagar vinculada">
                                <a className="link link-primary" href={`/financial/billings/${transfer.billingId}`}>
                                    #{transfer.billingId}
                                </a>
                            </Field>
                        )}
                    </div>
                </div>
            </div>

            {showApprove && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-2">Aprovar transferência</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Ao aprovar, a transferência será executada imediatamente no provedor. Confirme sua senha para liberar.
                        </p>
                        <PasswordField value={password} onChange={setPassword} disabled={processing} />
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setShowApprove(false); setPassword('') }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={handleApprove}
                                disabled={processing || !password}
                            >
                                {processing ? <span className="loading loading-spinner loading-sm" /> : 'Aprovar'}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => !processing && setShowApprove(false)} />
                </dialog>
            )}

            {showCancel && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-2">Cancelar transferência</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            A transferência será marcada como cancelada e não será executada. Esta ação não pode ser desfeita.
                        </p>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Motivo (opcional)</span>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={3}
                                placeholder="Ex: erro nos dados do destinatário"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setShowCancel(false); setCancelReason('') }}
                                disabled={processing}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-warning"
                                onClick={handleCancel}
                                disabled={processing}
                            >
                                {processing ? <span className="loading loading-spinner loading-sm" /> : 'Cancelar transferência'}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => !processing && setShowCancel(false)} />
                </dialog>
            )}

            {showReject && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-4">Rejeitar transferência</h3>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Motivo da rejeição</span>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setShowReject(false); setReason('') }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleReject}
                                disabled={processing || !reason.trim()}
                            >
                                {processing ? <span className="loading loading-spinner loading-sm" /> : 'Rejeitar'}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => !processing && setShowReject(false)} />
                </dialog>
            )}
        </div>
    )
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
    return (
        <div className={wide ? 'md:col-span-2' : ''}>
            <p className="text-base-content/50 text-xs mb-0.5">{label}</p>
            <div>{children}</div>
        </div>
    )
}
